import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  setDoc,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { realPhoto } from '@/lib/placeholder-photo';
import type { BookingProfile, BookingRequest, BookingRates, SupporterEntry, ParticipationClaim } from '@/types/booking';

/**
 * Classify a user doc by account type + roles. Book Talent is for CONTENT
 * CREATORS only — consumers can never be booked as talent, and venues are
 * booked through the separate "Book Venue" tab.
 */
function classifyAccount(data: any): { isCreator: boolean; isVenue: boolean; isConsumer: boolean } {
  const at = (data?.accountType || '').toLowerCase();
  const roles: string[] = Array.isArray(data?.roles) ? data.roles : [];
  const isVenue = at === 'venue' || roles.includes('Venue');
  const isConsumer =
    at === 'consumer' || at === 'consumer-business' ||
    roles.includes('Consumer') || roles.includes('Business Consumer');
  const isCreator = at === 'creator' || at === 'admin' || at === 'superadmin' || roles.includes('Creator');
  return { isCreator, isVenue, isConsumer };
}

/** Map a users/* doc to the internal booking-profile shape (with private flags). */
function toBookingProfileInternal(id: string, data: any): BookingProfile & { _hidden?: boolean; _venue?: boolean; _consumer?: boolean } {
  const { isVenue, isConsumer } = classifyAccount(data);
  return {
    userId: id,
    displayName: data.displayName || data.firstName || 'Unknown',
    profileImageUrl: realPhoto(data.profileImageUrl) || undefined,
    bookingRoles: data.bookingRoles || data.roles || [],
    bookingBio: data.bookingBio || undefined,
    bookingCity: data.bookingCity || data.address?.city || undefined,
    bookingAvailability: data.bookingAvailability || undefined,
    rates: data.bookingRates || undefined,
    availableForBooking: data.availableForBooking ?? true,
    genres: data.genres || [],
    instruments: Array.isArray(data.instruments) ? data.instruments : [],
    followerCount: data.followerCount || 0,
    verified: data.isIdVerified || false,
    portfolioUrl: data.portfolioUrl || undefined,
    bookingRatingAvg: data.bookingRatingCount > 0
      ? Math.round((data.bookingRatingSum / data.bookingRatingCount) * 10) / 10
      : undefined,
    bookingRatingCount: data.bookingRatingCount || 0,
    _hidden: data.hidden === true || data.adminHidden === true || data.accountType === 'ghost',
    _venue: isVenue,
    _consumer: isConsumer,
  } as BookingProfile & { _hidden?: boolean; _venue?: boolean; _consumer?: boolean };
}

/** Strip internal flags before returning a clean BookingProfile to callers. */
function stripInternal(p: any): BookingProfile {
  const { _hidden, _venue, _consumer, ...rest } = p;
  return rest as BookingProfile;
}

/**
 * Fetch bookable talent, optionally filtered by role.
 *
 * Filtering policy:
 *   • Query is purely role-based — no Firestore-level availableForBooking
 *     filter. Per Gordon's "users that are DJ's should show up in the
 *     marketplace in DJ Bookings" — gating on availableForBooking===true
 *     kept the directory empty for creators who simply hadn't toggled the
 *     opt-in flag yet, even though their account explicitly declared a role.
 *   • Public callers get every role-matched user EXCEPT those who explicitly
 *     opted out with availableForBooking === false. Missing/undefined is
 *     treated as "available by default" so newly-created creator profiles
 *     show up automatically.
 *   • adminAll=true bypasses the opt-out filter so super admin always sees
 *     every creator with the role, for moderation and outreach.
 */
/**
 * Public talent/venue directory for logged-OUT visitors. The `users` collection
 * is auth-only, so visitors can't run getTalentByRole directly — they'd get an
 * empty marketplace. This hits the public Admin-SDK API which returns only safe
 * booking fields. Use for visitors; authenticated users use getTalentByRole.
 */
export async function getPublicTalent(
  role: string | null,
  maxItems = 50,
  kind: 'talent' | 'venue' = 'talent',
): Promise<BookingProfile[]> {
  try {
    const params = new URLSearchParams({ kind, max: String(maxItems) });
    if (role) params.set('role', role);
    const res = await fetch(`/api/marketplace/talent?${params.toString()}`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.talent) ? (data.talent as BookingProfile[]) : [];
  } catch {
    return [];
  }
}

/** Visitors can't read `users`: the public directory (safe fields only) serves them. */
async function publicDirectory(kind: 'talent' | 'venue', role: string | null, maxItems: number): Promise<BookingProfile[]> {
  try {
    const qs = new URLSearchParams({ kind, max: String(Math.min(maxItems, 100)), ...(role && role !== 'all' ? { role } : {}) });
    const res = await fetch(`/api/marketplace/talent?${qs}`);
    const d = res.ok ? await res.json() : { talent: [] };
    return (d.talent || []) as BookingProfile[];
  } catch { return []; }
}

export async function getTalentByRole(
  role: string | null,
  maxItems = 50,
  adminAll = false,
): Promise<BookingProfile[]> {
  // Book Talent is open to visitors (Gordon 2026-11-03).
  if (!auth.currentUser) return publicDirectory('talent', role, maxItems);
  try {
    const usersRef = collection(db, 'users');
    let q;

    if (!role || role === 'all') {
      // Fetch wider than maxItems: consumers/venues get filtered out below, so
      // a plain limit(maxItems) could come back mostly non-creators. We slice
      // back to maxItems after filtering.
      q = query(usersRef, limit(Math.min(Math.max(maxItems * 5, maxItems), 300)));
    } else if (role === 'engineer') {
      q = query(usersRef, where('roles', 'array-contains-any', ['Recording Engineer', 'Mixing Engineer', 'Mastering Engineer']), limit(maxItems));
    } else {
      q = query(usersRef, where('roles', 'array-contains', role), limit(maxItems));
    }

    const snap = await getDocs(q);
    return snap.docs.map(d => toBookingProfileInternal(d.id, d.data()))
    .filter(p => {
      const hidden = (p as any)._hidden === true;
      // Book Talent = content creators ONLY. Consumers are never bookable as
      // talent, and venues are booked via the separate "Book Venue" tab.
      if ((p as any)._consumer || (p as any)._venue) return false;
      if (adminAll) {
        // Admin view: every creator with the role, including those who opted
        // out — but still skip ghost / admin-hidden accounts so the directory
        // doesn't surface placeholder rows.
        return !hidden && Array.isArray(p.bookingRoles) && p.bookingRoles.length > 0;
      }
      // Public view: hide only ghost / admin-hidden. Gordon: "don't hide
      // bookable users" — do NOT filter on the availableForBooking opt-out;
      // every role-matched creator shows (availability is displayed on the card).
      return !hidden;
    })
    .map(stripInternal)
    .slice(0, maxItems);
  } catch (err) {
    console.error('[booking] getTalentByRole error:', err);
    return [];
  }
}

/**
 * Fetch bookable VENUES for the separate "Book Venue" tab. Venues are users
 * with accountType 'venue' / roles containing 'Venue' (onboarding sets
 * roles: ['Venue']). Same opt-out + hidden rules as talent.
 */
export async function getVenuesForBooking(maxItems = 50, adminAll = false): Promise<BookingProfile[]> {
  if (!auth.currentUser) return publicDirectory('venue', null, maxItems);
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('roles', 'array-contains', 'Venue'), limit(maxItems));
    const snap = await getDocs(q);
    return snap.docs.map(d => toBookingProfileInternal(d.id, d.data()))
      .filter(p => {
        const hidden = (p as any)._hidden === true;
        if (!(p as any)._venue) return false; // venues only
        if (adminAll) return !hidden;
        return !hidden && p.availableForBooking !== false;
      })
      .map(stripInternal)
      .slice(0, maxItems);
  } catch (err) {
    console.error('[booking] getVenuesForBooking error:', err);
    return [];
  }
}

function mapRequest(id: string, data: any): BookingRequest {
  return {
    id,
    clientId: data.clientId || '',
    clientName: data.clientName || data.name || 'Someone',
    clientEmail: data.clientEmail || data.email || '',
    ...(data.clientPhone || data.phone ? { clientPhone: String(data.clientPhone || data.phone) } : {}),
    ...(data.bbCustomerOptIn ? { bbCustomerOptIn: true } : {}),
    talentId: data.talentId || '',
    talentName: data.talentName || '',
    proposedDate: data.proposedDate,
    proposedFee: data.proposedFee,
    currency: data.currency,
    eventType: data.eventType,
    location: data.location,
    message: data.message || '',
    status: data.status || 'pending',
    createdAt: data.createdAt,
  };
}

/**
 * Incoming booking requests for one talent (the logged-in creator sees only
 * requests addressed to them).
 */
export async function getIncomingBookings(talentId: string, maxItems = 100): Promise<BookingRequest[]> {
  try {
    const snap = await getDocs(query(collection(db, 'bookingRequests', talentId, 'requests'), limit(maxItems)));
    return snap.docs.map(d => mapRequest(d.id, d.data()))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  } catch (err) {
    console.error('[booking] getIncomingBookings error:', err);
    return [];
  }
}

/** ALL booking requests across the platform — super-admin only. */
export async function getAllBookings(maxItems = 300): Promise<BookingRequest[]> {
  try {
    const snap = await getDocs(query(collectionGroup(db, 'requests'), limit(maxItems)));
    return snap.docs
      .map(d => mapRequest(d.id, d.data()))
      // collectionGroup 'requests' could match other features — keep only booking ones
      .filter(r => r.talentId || r.clientName)
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  } catch (err) {
    console.error('[booking] getAllBookings error:', err);
    return [];
  }
}

/** Update a booking request's status (talent or admin). */
export async function updateBookingStatus(talentId: string, requestId: string, status: BookingRequest['status']): Promise<void> {
  await updateDoc(doc(db, 'bookingRequests', talentId, 'requests', requestId), { status });
}

/**
 * Submit a booking enquiry to the talent's subcollection.
 */
export async function submitBookingRequest(
  talentId: string,
  data: Omit<BookingRequest, 'id' | 'createdAt' | 'status'>
): Promise<string> {
  const ref = await addDoc(collection(db, 'bookingRequests', talentId, 'requests'), {
    ...data,
    status: 'pending',
    createdAt: Timestamp.now(),
  });
  return ref.id;
}

/**
 * Persist booking-related fields onto the user's own document.
 */
export async function saveBookingProfile(
  userId: string,
  profile: Partial<BookingProfile & { bookingRates: BookingRates }>
): Promise<void> {
  await setDoc(doc(db, 'users', userId), profile, { merge: true });
}

/**
 * Return the top supporters sorted by total amount descending.
 */
export async function getTopSupporters(maxItems = 50): Promise<SupporterEntry[]> {
  try {
    const snap = await getDocs(
      query(collection(db, 'supporters'), orderBy('totalAmount', 'desc'), limit(maxItems))
    );
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as SupporterEntry));
  } catch (err) {
    console.error('[booking] getTopSupporters error:', err);
    return [];
  }
}

/**
 * Write a new supporter entry.
 */
export async function addSupporter(entry: Omit<SupporterEntry, 'id'>): Promise<string> {
  const ref = await addDoc(collection(db, 'supporters'), entry);
  return ref.id;
}

/**
 * Fetch participation claims for a given item (track, release, event, etc.).
 */
export async function getParticipationClaims(itemId: string): Promise<ParticipationClaim[]> {
  try {
    const snap = await getDocs(collection(db, 'participationClaims', itemId, 'claims'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as ParticipationClaim));
  } catch (err) {
    console.error('[booking] getParticipationClaims error:', err);
    return [];
  }
}

/**
 * Submit a credit/participation claim for review.
 */
export async function submitParticipationClaim(
  itemId: string,
  claim: Omit<ParticipationClaim, 'id' | 'claimedAt' | 'status'>
): Promise<string> {
  const ref = await addDoc(collection(db, 'participationClaims', itemId, 'claims'), {
    ...claim,
    status: 'pending',
    claimedAt: Timestamp.now(),
  });
  return ref.id;
}

/**
 * Approve a participation claim.
 */
export async function approveParticipationClaim(itemId: string, claimId: string): Promise<void> {
  await updateDoc(doc(db, 'participationClaims', itemId, 'claims', claimId), {
    status: 'approved',
  });
}
